/**
 * Materialize a list of `PendingTargeted` (in-memory crops) into Targeted
 * MediaImages persisted to GCS / DB.
 *
 * Used by ad-creation/edit surfaces (RDAEditor, DemandGenAdEditor, etc.)
 * that pass image IDs upward to a parent form. Those flows materialize
 * inline so the parent receives concrete IDs to embed in its request.
 *
 * The PMax surface (`ImageAssetsSection`) defers materialization until
 * "Upload to Google Ads" click time. It uses the same `createTargetedImage`
 * call directly because it reads `memberUuid` from a prop rather than the
 * auth store; the partial-failure cleanup pattern below is mirrored there.
 *
 * Partial-failure semantics: parallel `createTargetedImage` calls — if any
 * fails (e.g. resize cap, upstream GCS error), the others may still have
 * succeeded and persisted Targeted rows + GCS blobs. We best-effort
 * `batchDeleteImages` the successes before rejecting, so the "no orphan
 * Targeted unless the user successfully uploads" invariant holds. Cleanup
 * is best-effort: a failed cleanup is logged and swallowed (the original
 * rejection is what callers care about).
 */
import { useCallback } from 'react';

import { useAuthStore } from '@/store/authStore';
import { batchDeleteImages, createTargetedImage } from '@/services/api/media';
import type { MediaImage, PendingTargeted } from '@/types/media';

export function useMaterializePendingTargeted() {
  const memberUuid = useAuthStore((s) => s.memberUuid);

  return useCallback(
    async (pending: PendingTargeted[]): Promise<MediaImage[]> => {
      if (pending.length === 0) return [];
      if (!memberUuid) {
        throw new Error('Not authenticated; cannot materialize Targeted images.');
      }
      // Parallel — each call is GCS-bound and independent. Serializing
      // would be O(n × ~3s), unacceptable for 5+ images.
      const settled = await Promise.allSettled(
        pending.map((p) =>
          createTargetedImage(p.source.id, {
            member_uuid: memberUuid,
            placement_key: p.placementKey,
            crop_box: p.cropBox,
          }),
        ),
      );

      const fulfilled = settled.flatMap((r) =>
        r.status === 'fulfilled' ? [r.value] : [],
      );
      const firstError = settled.find(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      );

      if (firstError) {
        // Best-effort cleanup of the rows that DID land. Swallow cleanup
        // errors — the caller needs to see the original failure, not the
        // cleanup failure.
        if (fulfilled.length > 0) {
          try {
            await batchDeleteImages({
              member_uuid: memberUuid,
              image_ids: fulfilled.map((m) => m.id),
            });
          } catch (cleanupErr) {
            console.warn(
              'useMaterializePendingTargeted: cleanup failed for partial successes',
              fulfilled.map((m) => m.id),
              cleanupErr,
            );
          }
        }
        throw firstError.reason instanceof Error
          ? firstError.reason
          : new Error(String(firstError.reason));
      }

      return fulfilled;
    },
    [memberUuid],
  );
}
